import { HttpTransport, API_BASE, USE_MOCKS } from './http';
import { createHttpApi } from './client';
import { createMockApi } from './mock';
import type { MailApi } from './contract';

export * from './types';
export * from './errors';
export * from './contract';
export { ApiError, errorEnvelope, API_BASE, USE_MOCKS, HttpTransport } from './http';
export { CSRF_HEADER, CSRF_COOKIE_NAMES, readCsrfToken, isStateChangingMethod } from './csrf';
export { onSessionSignal, emitSessionSignal } from './sessionSignals';
export type { SessionSignal } from './sessionSignals';

/**
 * There is no token here, and there must never be one again.
 *
 * The scaffold kept a session token in `localStorage` and sent it as
 * `Authorization: Bearer`. ADR 0001 rejects that design: the session token and
 * the envelope unlock key live in `__Host-ols_session` / `__Host-ols_key`, both
 * `HttpOnly`, so no script can read them — which matters because v1 renders
 * untrusted HTML mail, and anything readable by our own JS is readable by an
 * XSS payload in a message body.
 *
 * The consequence to design around, not to paper over: **the frontend cannot
 * see whether it is signed in.** Session state is derived from
 * `GET /auth/session` plus observed 401s — see `src/app/session.tsx`.
 */
export const transport = new HttpTransport(API_BASE);

/**
 * The app-wide API instance.
 *
 * VITE_USE_MOCKS=true swaps in the in-memory adapter so the whole UI runs
 * standalone with no backend. Both implementations satisfy the same `MailApi`
 * contract, so nothing downstream knows the difference.
 */
export const api: MailApi = USE_MOCKS
  ? createMockApi({
      extraSessions: Number(import.meta.env.VITE_MOCK_EXTRA_SESSIONS ?? 0) || 0,
      // MOCK: draw the Admin entry, for the accessibility suite (e2e/a11y),
      // which answers the admin API itself with page.route.
      admin: import.meta.env.VITE_MOCK_ADMIN === 'true',
      // MOCK: sends keep their copy to Sent, to see the sent-copy banner (#84).
      ...(import.meta.env.VITE_MOCK_SENT_COPY === 'pending' ||
      import.meta.env.VITE_MOCK_SENT_COPY === 'failed'
        ? { sentCopyFailure: import.meta.env.VITE_MOCK_SENT_COPY }
        : {}),
    })
  : createHttpApi(transport);

export type { MailApi };
